import { ReturnBearZod } from "../../return";

import {
  RedoShopifyCountryCode,
  tryCoalesceStringToRedoShopifyCountryCode,
} from "../../fulfillments/fulfillment-country-code";

interface ReturnWithReturnBear {
  returnBear?: ReturnBearZod | null;
}

/**
 * Checks if the return has ReturnBear data.
 * Verifies that returnBear exists and has a returnBearCaseId.
 */
export function hasReturnBear(return_: ReturnWithReturnBear): boolean {
  return !!return_.returnBear?.returnBearCaseId;
}

/**
 * Checks if the return has a ReturnBear QR code (used for drop-off/boxless returns).
 */
export function hasReturnBearQrCode(return_: ReturnWithReturnBear): boolean {
  return !!return_.returnBear?.qrCodeUri;
}

/**
 * Checks if the return has a ReturnBear shipping label.
 */
export function hasReturnBearLabel(return_: ReturnWithReturnBear): boolean {
  return !!return_.returnBear?.shippingInfo?.shippingLabelUrl;
}

/**
 * Gets the ReturnBear QR code URI if available.
 */
export function getReturnBearQrCodeUri(
  return_: ReturnWithReturnBear,
): string | undefined {
  return return_.returnBear?.qrCodeUri || undefined;
}

/**
 * Gets the ReturnBear shipping label URL if available.
 */
export function getReturnBearLabelUrl(
  return_: ReturnWithReturnBear,
): string | undefined {
  return return_.returnBear?.shippingInfo?.shippingLabelUrl || undefined;
}

/**
 * Gets the ReturnBear case ID if available.
 */
export function getReturnBearCaseId(
  return_: ReturnWithReturnBear,
): string | undefined {
  return return_.returnBear?.returnBearCaseId || undefined;
}

/**
 * Gets the ReturnBear case short ID if available.
 */
export function getReturnBearCaseShortId(
  return_: ReturnWithReturnBear,
): string | undefined {
  return return_.returnBear?.returnBearCaseShortId || undefined;
}

/**
 * Gets the ReturnBear tracking information if available.
 */
export function getReturnBearTracking(
  return_: ReturnWithReturnBear,
):
  | { carrierName: string; trackingNumber: string; trackingUrl: string }
  | undefined {
  const shippingInfo = return_.returnBear?.shippingInfo;
  if (!shippingInfo) {
    return undefined;
  }

  return {
    carrierName: shippingInfo.carrierName,
    trackingNumber: shippingInfo.trackingNumber,
    trackingUrl: shippingInfo.trackingUrl,
  };
}

/**
 * Default label markup amount (in USD) for all routes that are not explicitly configured.
 * This is added on top of the ReturnBear base rate when calculating the customer-facing shipping fee.
 */
export const DEFAULT_RETURNBEAR_MARKUP_USD = 5;

export class ReturnBearMarkupKey {
  private constructor() {}

  static encode(
    origin: RedoShopifyCountryCode,
    destination: RedoShopifyCountryCode,
  ): string {
    return `${origin}_${destination}`;
  }

  static decode(key: string): {
    fromCountry: RedoShopifyCountryCode;
    toCountry: RedoShopifyCountryCode;
  } {
    const [fromCountry, toCountry] = key.split("_");
    if (!fromCountry || !toCountry) {
      throw new Error(`Invalid ReturnBear markup key format: "${key}"`);
    }

    const fromCountryCode =
      tryCoalesceStringToRedoShopifyCountryCode(fromCountry);
    const toCountryCode = tryCoalesceStringToRedoShopifyCountryCode(toCountry);

    if (!fromCountryCode || !toCountryCode) {
      throw new Error(
        `Invalid country codes in ReturnBear markup key: "${key}" (from: ${fromCountry}, to: ${toCountry})`,
      );
    }

    return { fromCountry: fromCountryCode, toCountry: toCountryCode };
  }
}
